import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { ArrowLeft, Info, X } from '@phosphor-icons/react'
import {
  Button,
  CheckboxV2,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  IconButton,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@shopflo/ui'
import SearchInput from 'src/components/common/search-and-filters/subcomponents/SearchInput'
import { UpsellDowngradePlanDialogProps } from 'src/lib/types/appsIntegrations'
import { useLocale } from 'src/lib/hooks/useLocal'

export const UpsellDowngradePlanDialog: React.FC<UpsellDowngradePlanDialogProps> = ({ state, actions }) => {
  const { isOpen, upsells, activeUpsellsCount, isLoading = false, maxAllowedActiveUpsellsOnLite } = state
  const { onBack, onClose, onContinue } = actions
  const { t } = useLocale()
  const [searchText, setSearchText] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  // The dialog stays mounted, and the upsells only arrive with the FSE_001 error — so (re)build the default
  // selection each time it opens with data, rather than once at mount when the list is still empty.
  useEffect(() => {
    if (!isOpen) return
    setSearchText('')
    setSelectedIds(
      upsells
        .filter(upsell => upsell.is_active)
        .slice(0, Math.min(maxAllowedActiveUpsellsOnLite, activeUpsellsCount))
        .map(upsell => upsell.id)
    )
  }, [isOpen, upsells, maxAllowedActiveUpsellsOnLite, activeUpsellsCount])

  const filteredUpsells = useMemo(() => {
    const query = searchText.trim().toLowerCase()
    if (!query) return upsells
    return upsells.filter(upsell => upsell.title.toLowerCase().includes(query))
  }, [upsells, searchText])

  const isMaxReached = selectedIds.length >= maxAllowedActiveUpsellsOnLite

  const handleToggleUpsell = (id: string, checked: boolean) => {
    setSelectedIds(prev => {
      if (checked) {
        if (prev.includes(id) || prev.length >= maxAllowedActiveUpsellsOnLite) return prev
        return [...prev, id]
      }
      return prev.filter(existingId => existingId !== id)
    })
  }

  const handleContinue = () => {
    onContinue(selectedIds)
  }

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && !isLoading && onClose()}>
      <DialogContent
        closeOnOverlayClick={false}
        className='bg-white w-[560px] max-w-[calc(100vw-2rem)] !rounded-2xl !p-0 !gap-0 [&>button]:hidden'
      >
        <DialogHeader className='px-5 pt-4 pb-0'>
          <div className='flex items-center justify-between gap-4'>
            <div className='flex items-center gap-2.5'>
              <IconButton
                variant='outline'
                size='sm'
                IconComponent={ArrowLeft}
                aria-label='Back'
                className='text-zinc-600'
                iconProps={{ weight: 'bold' }}
                onClick={onBack}
                disabled={isLoading}
              />
              <DialogTitle className='!text-base !font-semibold !text-zinc-800'>
                {t('upsell_downgrade_title')}
              </DialogTitle>
            </div>
            <IconButton
              variant='ghost'
              size='sm'
              IconComponent={X}
              aria-label='Close'
              className='-mr-1.5 text-zinc-500'
              onClick={onClose}
              disabled={isLoading}
            />
          </div>
        </DialogHeader>

        <div className='px-5 pt-4 pb-5 flex flex-col gap-3'>
          <div className='flex items-start gap-2 rounded-lg bg-sky-50 px-3 py-2.5'>
            <Icon IconComponent={Info} size='sm' className='mt-0.5 flex-shrink-0 text-sky-600' />
            <p className='text-text-sm-regular text-sky-900'>
              Lite supports{' '}
              <span className='text-text-sm-semibold'>{maxAllowedActiveUpsellsOnLite} active upsells</span>. Below are
              your currently active upsells—select {maxAllowedActiveUpsellsOnLite} to keep.
            </p>
          </div>

          <SearchInput
            searchText={searchText}
            onChange={setSearchText}
            params={[
              {
                value: 'upsell',
                label: t('upsell_downgrade_search_label'),
                placeholder: t('upsell_downgrade_search_placeholder')
              }
            ]}
            selectedParam='upsell'
          />

          <div className='flex max-h-[360px] flex-col overflow-y-auto'>
            {filteredUpsells.map(upsell => {
              const isChecked = selectedIds.includes(upsell.id)
              const isDisabled = !isChecked && isMaxReached

              return (
                <div key={upsell.id} className='flex items-center gap-3 border-b border-zinc-100 py-3 last:border-b-0'>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className='inline-flex'>
                        <CheckboxV2
                          checked={isChecked}
                          disabled={isDisabled}
                          size='sm'
                          onCheckedChange={checked => handleToggleUpsell(upsell.id, checked === true)}
                        />
                      </span>
                    </TooltipTrigger>
                    {isDisabled && (
                      <TooltipContent align='start'>
                        Maximum {maxAllowedActiveUpsellsOnLite} active upsells.
                      </TooltipContent>
                    )}
                  </Tooltip>

                  {upsell.image_url ? (
                    <img
                      src={upsell.image_url}
                      alt=''
                      className='h-9 w-9 flex-shrink-0 rounded-md border border-zinc-100 object-cover'
                    />
                  ) : (
                    <div className='h-9 w-9 flex-shrink-0 rounded-md border border-zinc-100 bg-zinc-50' />
                  )}

                  <p className='flex-1 truncate text-text-sm-medium text-zinc-800'>{upsell.title}</p>

                  <p className='whitespace-nowrap text-text-xs-regular text-zinc-500'>
                    Created on {moment(upsell.created_at).format('DD-MM-YYYY')}
                  </p>
                </div>
              )
            })}

            {filteredUpsells.length === 0 && (
              <p className='py-6 text-center text-text-sm-regular text-zinc-500'>
                {t('upsell_downgrade_no_upsells_found')}
              </p>
            )}
          </div>
        </div>

        <DialogFooter className='px-5 pb-5 pt-3 flex items-center justify-between'>
          <p className='text-text-sm-regular text-zinc-500'>
            {selectedIds.length} of {maxAllowedActiveUpsellsOnLite} selected
          </p>
          <div className='flex items-center gap-3'>
            <Button variant='outline' size='md' onClick={onBack} disabled={isLoading}>
              {t('upsell_downgrade_cancel')}
            </Button>
            <Button variant='primary' size='md' onClick={handleContinue} disabled={isLoading}>
              {t('upsell_downgrade_continue_with_lite')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
